import * as React from 'react';
import { List, RaRecord, useNotify } from 'react-admin';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Divider,
  Button,
  Stack,
  Chip,
  Card,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOrdersList } from '../../hooks/useOrderStatus';
import { useOrdersListVendor } from '../../hooks/useOrderStatusVendor';
import { CourierProvider, OrderService } from '../../services/order/orderService';
import { EstimatedTime } from '../../enums/EstimatedTime';
import { useState } from 'react';
import { EstimatedTimeDialog } from '../../components/dialogs/EstimatedTimeDIalog';
import { playNotificationSound } from '../../utils/sound';
import { OrderStatus } from '../../enums/OrderStatus';
import { LocationService } from '../../services/location/locationService';
import { PageHeader } from '../../components/page-header/PageHeader';
import * as Sentry from '@sentry/react';
import { hasRole } from '../../utils/roles';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { DeliveryTracking } from './DeliveryTracking';
import { DeliveryInfo } from './deliveryTimeline';
import { OrderNote } from '../../components/order-note/OrderNote';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { CourierChoiceDialog, OwnCourierActions, OwnCourierInfo } from './OwnCourier';

type Order = RaRecord & {
  id: number | string;
  orderNumber?: string;
  status: string;
  createdAt?: string;
  paymentMethod?: string;
  pickupTime?: string;
  customer?: { name?: string; phone?: string; address?: string };
  items?: { name: string; price: number; qty?: number }[];
  fees?: { label: string; amount: number }[];
  total?: number;
  assignedCourierId?: number;
  restaurantId?: number;
  orderType?: string;
  delivery?: DeliveryInfo;
  courierProvider?: CourierProvider | null;
  ownCouriersEnabled?: boolean;
};

const OrdersActions: React.FC<{ isConnected: boolean; error: string | null }> = ({
  isConnected,
  error,
}) => {
  const { t } = useTranslation();

  const statusPill = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        px: 'var(--space-3)',
        py: '6px',
        borderRadius: 'var(--radius-pill)',
        backgroundColor: isConnected ? 'var(--color-success-soft)' : 'var(--color-error-soft)',
        color: isConnected ? 'var(--color-success)' : 'var(--color-error)',
        fontSize: 'var(--text-xs)',
        fontWeight: 700,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: isConnected ? 'var(--color-success)' : 'var(--color-error)',
          boxShadow: isConnected
            ? '0 0 0 3px rgba(22,163,74,0.18)'
            : '0 0 0 3px rgba(220,38,38,0.18)',
        }}
      />
      {isConnected
        ? t('orders.live', { defaultValue: 'Live' })
        : t('orders.disconnected', { defaultValue: 'Disconnected' })}
    </Box>
  );

  return (
    <PageHeader
      title={t('resources.orders')}
      subtitle={t('orders.subtitle', { defaultValue: 'Incoming orders appear here in real time.' })}
      leading={statusPill}
      actions={
        error ? (
          <Typography
            sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)', fontWeight: 600 }}
          >
            {error}
          </Typography>
        ) : null
      }
    />
  );
};

export default function OrdersScreen(): JSX.Element {
  useNotify();
  // VENDOR i RESTAURANT_OWNER widza tylko swoj lokal - backend nie wpusci ich na liste globalna.
  const isLocationScoped = hasRole('VENDOR', 'RESTAURANT_OWNER');
  const [locationId, setLocationId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!isLocationScoped) return;
    new LocationService().getLocations().then((locations) => setLocationId(locations[0].id));
  }, [isLocationScoped]);

  if (isLocationScoped && locationId === null) {
    return (
      <>
        <OrdersActions isConnected={false} error={null} />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={36} />
        </Box>
      </>
    );
  }

  return isLocationScoped ? (
    <LocationOrdersScreen locationId={locationId!} />
  ) : (
    <AdminOrdersScreen />
  );
}

const LocationOrdersScreen: React.FC<{ locationId: number }> = ({ locationId }) => {
  const { orders, isConnected, error } = useOrdersListVendor(locationId);
  return <OrdersScreenContent orders={orders} isConnected={isConnected} error={error} />;
};

const AdminOrdersScreen: React.FC = () => {
  const { orders, isConnected, error } = useOrdersList();
  return <OrdersScreenContent orders={orders} isConnected={isConnected} error={error} />;
};

const OrdersScreenContent: React.FC<{
  orders: any[];
  isConnected: boolean;
  error: string | null;
}> = ({ orders, isConnected, error }) => {
  return (
    <>
      <OrdersActions isConnected={isConnected} error={error} />
      <List
        actions={false}
        queryOptions={{ enabled: false }}
        pagination={false}
        sx={{
          '& .RaList-main': {
            px: PAGE_GUTTER,
            pb: 'var(--space-8)',
          },
          '& .RaList-content': {
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-sm)',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface)',
          },
        }}
      >
        <OrdersLayoutContent wsOrders={orders} isConnected={isConnected} />
      </List>
    </>
  );
};

const OrdersLayoutContent: React.FC<{ wsOrders: any[]; isConnected: boolean }> = ({
  wsOrders,
  isConnected,
}) => {
  const [selectedTab, setSelectedTab] = React.useState<'new' | 'in_progress'>('new');
  const [selectedId, setSelectedId] = React.useState<number | string | null>(null);
  const { t } = useTranslation();

  const prevNewOrdersCountRef = React.useRef(0);
  const isInitialLoadRef = React.useRef(true);

  React.useEffect(() => {
    const newOrdersCount = wsOrders.filter((order) => order.orderStatus === 'PAID').length;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      prevNewOrdersCountRef.current = newOrdersCount;
      return;
    }

    if (newOrdersCount > prevNewOrdersCountRef.current) {
      playNotificationSound();
    }

    prevNewOrdersCountRef.current = newOrdersCount;
  }, [wsOrders]);

  React.useEffect(() => {
    const pendingOrders = wsOrders.filter((order) => order.orderStatus === 'PAID');

    if (pendingOrders.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      playNotificationSound();
    }, 10 * 1000);

    return () => clearInterval(interval);
  }, [wsOrders]);

  const allOrders: Order[] = React.useMemo(() => {
    return wsOrders.map((order) => ({
      id: order.id,
      orderNumber: order.number,
      status: order.orderStatus,
      createdAt: order.timestamp,
      paymentMethod: order.paymentType,
      customerInformation: order.customerInformation,
      pickupTime: order.pickupTime,
      orderDetails: order.orderDetails,
      restaurantId: order.restaurantId,
      isReadyForPickup: order.isReadyForPickup,
      orderType: order.orderType,
      delivery: {
        statusHistory: order.statusHistory,
        deliveryEvents: order.deliveryEvents,
        woltTrackingUrl: order.woltTrackingUrl,
        pickupEta: order.pickupEta,
        dropoffEtaMin: order.dropoffEtaMin,
        dropoffEtaMax: order.dropoffEtaMax,
        woltRetryUntil: order.woltRetryUntil,
      },
      courierProvider: order.courierProvider,
      ownCouriersEnabled: order.ownCouriersEnabled,
    }));
  }, [wsOrders]);

  const filteredOrders = React.useMemo(() => {
    return allOrders.filter((order) => {
      if (selectedTab === 'new') {
        return order.status === 'PAID';
      } else {
        return (
          order.status !== 'PAID' && order.status !== 'DELIVERED' && order.status !== 'CANCELLED'
        );
      }
    });
  }, [allOrders, selectedTab]);

  console.log('All Orders:', allOrders);

  const newCount = allOrders.filter((o) => o.status === 'PAID').length;

  const inProgressCount = allOrders.filter(
    (o) => o.status !== 'PAID' && o.status !== 'DELIVERED' && o.status !== 'CANCELLED',
  ).length;

  const isLoading = !isConnected;

  React.useEffect(() => {
    if (isLoading) return;
    if (!filteredOrders.length) {
      setSelectedId(null);
      return;
    }
    const exists = selectedId != null && filteredOrders.some((r) => r.id === selectedId);
    if (selectedId == null || !exists) {
      setSelectedId(filteredOrders[0].id);
    }
  }, [isLoading, filteredOrders, selectedTab]);

  const handleChangeTab = (_: any, value: 'new' | 'in_progress') => {
    setSelectedTab(value);
    setSelectedId(null);
  };

  const selectedRecord = filteredOrders.find((r) => r.id === selectedId);
  const isMobile = useMediaQuery('(max-width:900px)');

  console.log('isConnected', isConnected);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: { md: 700 },
        backgroundColor: 'var(--color-surface)',
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', md: 320 },
          borderRight: { md: '1px solid var(--color-border)' },
          borderBottom: { xs: '1px solid var(--color-border)', md: 'none' },
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleChangeTab}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
          }}
        >
          <Tab
            value="new"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{t('orders.new')}</span>
                <Chip size="small" color={newCount > 0 ? 'primary' : 'default'} label={newCount} />
              </Stack>
            }
          />
          <Tab
            value="in_progress"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{t('orders.inProgress')}</span>
                <Chip
                  size="small"
                  color={inProgressCount > 0 ? 'primary' : 'default'}
                  label={inProgressCount}
                />
              </Stack>
            }
          />
        </Tabs>
        <Divider />

        <OrdersListPanel
          data={filteredOrders}
          isLoading={isLoading}
          selectedId={selectedId}
          setSelectedId={(id) => setSelectedId(id)}
        />
      </Box>

      {/* On phones the three desktop columns stack vertically, so the info and
          details panels would render a wall of "—" placeholders under the list
          when nothing is selected. Show them only once there is a record. */}
      {(!isMobile || selectedRecord) && (
        <>
          <MiddleInfoPanel record={selectedRecord ?? undefined} selectedTab={selectedTab} />

          <RightDetailsPanel record={selectedRecord ?? undefined} />
        </>
      )}
    </Box>
  );
};

const CountdownCircle: React.FC<{ estimatedCompletionTime: string | null }> = ({
  estimatedCompletionTime,
}) => {
  const [minutesLeft, setMinutesLeft] = React.useState<number | null>(null);

  const parseCustomDate = (dateStr: string): Date | null => {
    try {
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('-');
      const [hour, minute] = timePart.split(':');

      const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:00Z`;

      const date = new Date(isoString);
      return isNaN(date.getTime()) ? null : date;
    } catch (error) {
      console.error('Error parsing date:', dateStr, error);
      Sentry.captureException(error);
      return null;
    }
  };

  React.useEffect(() => {
    if (!estimatedCompletionTime) {
      setMinutesLeft(null);
      return;
    }

    const completionTime = parseCustomDate(estimatedCompletionTime);

    if (!completionTime) {
      console.error('Invalid date format:', estimatedCompletionTime);
      setMinutesLeft(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = completionTime.getTime() - now.getTime();
      const minutes = Math.ceil(diff / (1000 * 60));
      setMinutesLeft(Math.max(0, minutes));
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [estimatedCompletionTime]);

  if (minutesLeft === null) return null;

  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: 'var(--color-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          color: 'black',
        }}
      >
        {minutesLeft}
      </Typography>
    </Box>
  );
};

const OrdersListPanel: React.FC<{
  data: Order[];
  isLoading?: boolean;
  selectedId: number | string | null;
  setSelectedId: (id: number | string) => void;
}> = ({ data, isLoading, selectedId, setSelectedId }) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }} aria-label="Loading orders">
        <CircularProgress size={36} />
      </Box>
    );
  }
  if (!data.length) return <Box sx={{ p: 2, color: 'text.secondary' }}>{t('orders.noOrders')}</Box>;

  return (
    <Box>
      {data.map((order) => {
        const active = selectedId === order.id;
        return (
          <Box
            key={order.id}
            onClick={() => setSelectedId(order.id)}
            sx={{
              cursor: 'pointer',
              px: 'var(--space-4)',
              py: 'var(--space-3)',
              borderLeft: active ? '3px solid var(--color-primary)' : '3px solid transparent',
              backgroundColor: active ? 'var(--color-primary-soft)' : 'transparent',
              '&:not(:last-of-type)': { borderBottom: '1px solid var(--color-hairline)' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'background-color var(--dur-fast) var(--ease-standard)',
              '&:hover': {
                backgroundColor: active ? 'var(--color-primary-soft)' : 'var(--color-bg-subtle)',
              },
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ gap: 'var(--space-2)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-base)' }}>
                #{order.orderNumber ?? order.id}
              </Typography>
              {order.orderDetails?.note && (
                <StickyNote2OutlinedIcon
                  titleAccess={t('orders.customerNote')}
                  sx={{ color: 'var(--color-warning)', fontSize: 18 }}
                />
              )}
            </Stack>
            <CountdownCircle estimatedCompletionTime={order.pickupTime ?? null} />
          </Box>
        );
      })}
    </Box>
  );
};

const MiddleInfoPanel: React.FC<{ record?: Order; selectedTab: any }> = ({
  record,
  selectedTab,
}) => {
  const status = record?.status ? record.status.toUpperCase() : '—';
  const payment = record?.paymentMethod ?? '—';
  const customerName = record?.customerInformation?.name ?? '—';
  const customerPhone = record?.customerInformation?.phoneNumber ?? '—';
  const customerEmail = record?.customerInformation?.email ?? '—';
  const customerAddress = record?.customerInformation?.defaultAddress ?? '—';
  const pickupTime = record?.pickupTime ?? '—';
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 320 },
        borderRight: { md: '1px solid var(--color-border)' },
        borderBottom: { xs: '1px solid var(--color-border)', md: 'none' },
      }}
    >
      <Box sx={{ px: 'var(--space-4)', py: 'var(--space-3)' }}>
        <Typography
          sx={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
            mb: '4px',
            fontWeight: 700,
            letterSpacing: 'var(--tracking-wide)',
            textTransform: 'uppercase',
          }}
        >
          {t('orders.status')}
        </Typography>
        <Typography
          sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-primary)', fontWeight: 700 }}
        >
          {OrderStatus[status as keyof typeof OrderStatus] || status}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ px: 'var(--space-4)', py: 'var(--space-3)' }}>
        <Typography
          sx={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
            mb: '4px',
            fontWeight: 700,
            letterSpacing: 'var(--tracking-wide)',
            textTransform: 'uppercase',
          }}
        >
          {t('orders.paymentType')}
        </Typography>
        <Typography
          sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-primary)', fontWeight: 700 }}
        >
          {payment}
        </Typography>
      </Box>

      <Divider />

      {status !== 'PAID' && selectedTab !== 'new' && (
        <>
          <Box sx={{ px: 'var(--space-4)', py: 'var(--space-3)' }}>
            <Typography
              sx={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
                mb: '4px',
                fontWeight: 700,
                letterSpacing: 'var(--tracking-wide)',
                textTransform: 'uppercase',
              }}
            >
              {t('orders.pickupTime')}
            </Typography>
            <Typography
              sx={{
                fontSize: 'var(--text-base)',
                color: 'var(--color-text-primary)',
                fontWeight: 700,
              }}
            >
              {pickupTime}
            </Typography>
          </Box>

          <Divider />
        </>
      )}

      {/* Po potwierdzeniu zamowienie z dostawa Woltem nie ma przyciskow - restauracja widzi, co
          robi kurier. Kuriera restauracji prowadzi sama restauracja (przyciski w szczegolach). */}
      {record?.orderType === 'DELIVERY' && status !== 'PAID' && record.courierProvider === 'RESTAURANT' && (
        <>
          <OwnCourierInfo status={status} />
          <Divider />
        </>
      )}
      {record?.orderType === 'DELIVERY' &&
        status !== 'PAID' &&
        record.courierProvider !== 'RESTAURANT' &&
        record.delivery && (
          <>
            <DeliveryTracking info={record.delivery} />
            <Divider />
          </>
        )}

      <Box sx={{ px: 'var(--space-4)', py: 'var(--space-3)' }}>
        <Typography
          sx={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
            mb: '6px',
            fontWeight: 700,
            letterSpacing: 'var(--tracking-wide)',
            textTransform: 'uppercase',
          }}
        >
          {t('orders.customerInformation')}
        </Typography>
        <Stack spacing="4px">
          <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 700 }}>
            {customerName}
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {customerPhone}
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {customerEmail}
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            {customerAddress}
          </Typography>
        </Stack>
      </Box>
      <Divider />
    </Box>
  );
};

const RightDetailsPanel: React.FC<{ record?: Order }> = ({ record }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:900px)');
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box>
        <Typography
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            p: 'var(--space-3)',
            fontSize: 'var(--text-md)',
          }}
        >
          {t('orders.orderDetails')}
        </Typography>
        <Divider />
      </Box>
      <Box sx={{ flex: 1, p: { xs: 'var(--space-4)', md: 'var(--space-6)' } }}>
        {!record ? (
          <Card
            variant="outlined"
            sx={{ p: 'var(--space-6)', textAlign: 'center', color: 'var(--color-text-tertiary)' }}
          >
            {isMobile ? t('orders.selectOrderFromListMobile') : t('orders.selectOrderFromList')}
          </Card>
        ) : (
          <OrderDetailsCard record={record} />
        )}
      </Box>
    </Box>
  );
};

const OrderDetailsCard: React.FC<{ record: Order }> = ({ record }) => {
  const items = record.orderDetails ?? [];
  const currency = items.currency ?? '€';
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [courierDialogOpen, setCourierDialogOpen] = useState<boolean>(false);
  const [courier, setCourier] = useState<CourierProvider | undefined>(undefined);
  const [_estimatedTime, setEstimatedTime] = useState<EstimatedTime | null>(null);
  // Lokal z wlasnymi kurierami wybiera przy akceptacji dostawy, kto wiezie - przed czasem przygotowania.
  const choosesCourier = record.orderType === 'DELIVERY' && record.ownCouriersEnabled === true;

  // Karta jest wspolna dla wszystkich zamowien z listy - wybor kuriera nie moze przejsc na inne.
  React.useEffect(() => {
    setCourier(undefined);
  }, [record.id]);
  const notify = useNotify();
  console.log('createdAt: ' + record.createdAt);

  const orderService = new OrderService();

  const cancelOrder = async (orderId: number) => {
    try {
      await orderService.cancelOrder(orderId);
    } catch (e: any) {
      console.error('Error cancelling order:', e);
      Sentry.captureException(e);
      notify(e?.response.data.error || t('notifications.internalError'), { type: 'error' });
    }
  };

  const confirmOrder = async (orderId: number, status: string, estimatedTime: string) => {
    if (status !== 'PAID') {
      return;
    }

    try {
      await orderService.confirmOrder(orderId, estimatedTime, choosesCourier ? courier : undefined);
    } catch (e: any) {
      console.error(e);
      Sentry.captureException(e);
      notify(e?.response.data.error || t('notifications.internalError'), { type: 'error' });
    }
  };

  const handleEstimatedTimeSelect = async (time: EstimatedTime) => {
    setEstimatedTime(time);

    await confirmOrder(Number(record.id), record.status, time);
  };

  const markOrderAsReady = async (orderId: number) => {
    const orderService = new OrderService();
    try {
      await orderService.markOrderAsReady(orderId);
    } catch (e: any) {
      notify(e?.response.data.error || t('notifications.internalError'), { type: 'error' });
      Sentry.captureException(e);
      console.error('Error marking order as ready:', e);
    }
  };

  const markOrderAsPickedUp = async (orderId: number) => {
    try {
      await orderService.markOrderAsPickedUp(orderId);
    } catch (e: any) {
      notify(e?.response.data.error || t('notifications.internalError'), { type: 'error' });
      Sentry.captureException(e);
      console.error('Error marking order as picked up:', e);
    }
  };

  const getEnglishItemName = (
    translations?: { language: string; translatedName: string }[],
  ): string => {
    return (
      translations?.find((t) => t.language === 'ENG')?.translatedName ??
      translations?.[0]?.translatedName ??
      '—'
    );
  };

  return (
    <Card variant="outlined" sx={{ p: { xs: 'var(--space-4)', md: 'var(--space-6)' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '16px', color: 'var(--color-text-disabled)' }}>
          {t('orders.orderNumber')}
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
          #{record.orderNumber ?? record.id}
        </Typography>
      </Stack>
      {record.createdAt && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '16px', color: 'var(--color-text-disabled)' }}>
            {t('orders.createdAt')}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
            {new Date(record.createdAt + 'Z').toLocaleString()}
          </Typography>
        </Stack>
      )}

      <Box sx={{ fontSize: 'var(--text-2xs)', color: 'text.secondary', mb: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '60% 40%',
            rowGap: 2,
            alignItems: 'start',
          }}
        >
          <Typography variant="overline" sx={{ color: 'var(--color-text-disabled)' }}>
            {t('orders.order')}
          </Typography>
          <Typography
            variant="overline"
            sx={{ color: 'var(--color-text-disabled)', textAlign: 'right' }}
          >
            {t('orders.price')}
          </Typography>

          {items.orderListItems.map((it: any, idx: number) => (
            <React.Fragment key={idx}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box
                  component="img"
                  src={it.imageUrl}
                  alt="item's image"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    objectFit: 'cover',
                  }}
                />
                <Box>
                  <Typography sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {getEnglishItemName(it.itemTranslations)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 'var(--text-2xs)', color: 'text.secondary', fontWeight: 700 }}
                  >
                    x{it.quantity}
                  </Typography>
                  {(it.inventoryItemVariants ?? []).map((v: any) => {
                    const label = [v.name, v.variant].filter(Boolean).join(' ');
                    const hasPrice = parseFloat(v.price) > 0;
                    return (
                      <Typography
                        key={v.id}
                        sx={{ fontSize: 'var(--text-2xs)', color: 'text.secondary' }}
                      >
                        {v.category ? `${v.category}: ` : '+ '}
                        {label || '—'}
                        {hasPrice ? ` (+${v.price})` : ''}
                      </Typography>
                    );
                  })}
                </Box>
              </Stack>

              <Typography sx={{ textAlign: 'right', fontWeight: 700 }}>
                {it.price} {currency}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {items.note && (
        <Box sx={{ mt: 'var(--space-4)' }}>
          <OrderNote note={items.note} />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack spacing={2} sx={{ fontSize: 'var(--text-2xs)' }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: 'text.secondary' }}>{t('orders.itemSubtotal')}</Typography>
          <Typography sx={{ fontWeight: 500 }}>
            {items.itemSubTotal} {currency}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: 'text.secondary' }}>{t('orders.delivery')}</Typography>
          <Typography sx={{ fontWeight: 500 }}>
            {items.deliveryFee} {currency}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>
            {t('orders.total')}
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {items.totalPrice} {currency}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'center' }}>
        {record.status === 'PAID' && (
          <Button
            variant="outlined"
            onClick={() => cancelOrder(Number(record.id))}
            sx={{
              borderRadius: 'var(--radius-md)',
              borderColor: 'var(--color-error)',
              color: 'var(--color-error)',
              '&:hover': {
                borderColor: 'var(--color-error-hover)',
                backgroundColor: 'var(--color-error-soft)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
        )}
        {record.status === 'PAID' && (
          <Button
            variant="contained"
            onClick={async () => {
              if (record.status === 'IN_PREPARATION' || record.status === 'ACCEPTED_BY_COURIER') {
                await markOrderAsReady(Number(record.id));
              }
              if (record.status === 'PAID') {
                if (choosesCourier) {
                  setCourierDialogOpen(true);
                } else {
                  setOpen(true);
                }
              }
            }}
            sx={{
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text-inverse)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-xs)',
              '&:hover': {
                backgroundColor: 'var(--color-primary-hover)',
                boxShadow: 'var(--shadow-md)',
              },
            }}
          >
            {t('orders.confirmOrder')}
          </Button>
        )}
        {record.orderType === 'DELIVERY' && record.courierProvider === 'RESTAURANT' && (
          <OwnCourierActions
            orderId={Number(record.id)}
            status={record.status}
            onError={(e) => {
              notify(e?.response?.data?.error || t('notifications.internalError'), { type: 'error' });
              Sentry.captureException(e);
            }}
          />
        )}
        {record.orderType === 'SELF_PICK_UP' && record.status === 'IN_PREPARATION' && (
          <Button
            variant="contained"
            onClick={() => markOrderAsReady(Number(record.id))}
            sx={{
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text-inverse)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-xs)',
              '&:hover': {
                backgroundColor: 'var(--color-primary-hover)',
                boxShadow: 'var(--shadow-md)',
              },
            }}
          >
            {t('orders.readyForPickup')}
          </Button>
        )}
        {record.orderType === 'SELF_PICK_UP' && record.status === 'READY_FOR_PICKUP' && (
          <Button
            variant="contained"
            onClick={() => markOrderAsPickedUp(Number(record.id))}
            sx={{
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text-inverse)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-xs)',
              '&:hover': {
                backgroundColor: 'var(--color-primary-hover)',
                boxShadow: 'var(--shadow-md)',
              },
            }}
          >
            {t('orders.pickedUp')}
          </Button>
        )}
      </Stack>
      {choosesCourier && (
        <CourierChoiceDialog
          open={courierDialogOpen}
          orderId={Number(record.id)}
          onClose={() => setCourierDialogOpen(false)}
          onSelect={(selected) => {
            setCourier(selected);
            setOpen(true);
          }}
        />
      )}
      <EstimatedTimeDialog
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleEstimatedTimeSelect}
      />
    </Card>
  );
};
